/**
 * 대화방을 무엇이라 부를지 (chat-room-name — 2026-08-26).
 *
 * 방 이름은 목록·대화 머리·정보 패널이 각자 그리고 있었다. 규칙을 여기 하나로 모은다 —
 * 화면마다 따로 계산하면 한 곳만 옛 이름으로 남는다.
 *
 * 고르는 순서:
 *   1. **내가 부르는 이름** — 내 쪽에만 붙는다(1:1 도 여기서 바뀐다).
 *   2. **공용 이름** — 방에 저장된 이름. 단체방은 방장·관리자가 정한다.
 *   3. **기본 이름** — 1:1 은 상대 이름, 그 밖은 '단체대화방'.
 *
 * 왜 1:1 은 공용 이름을 안 쓰나: 참여자가 둘뿐이라 방에 이름을 저장하면 **상대 화면의 이름까지**
 * 바뀐다. 서버도 그래서 1:1 방 이름 수정을 막는다(DIRECT_ROOM_IMMUTABLE).
 */

/** 이름을 정하는 데 필요한 최소한의 멤버 정보. */
export interface NameMember {
  userId: string;
  name?: string | null;
}

export const GROUP_FALLBACK_NAME = '단체대화방';

/** 1:1 방에서 상대 한 사람. 못 찾으면 null. */
export function otherMemberOf(
  members: readonly NameMember[] | undefined, myId: string | null | undefined,
): NameMember | null {
  if (!members || members.length === 0) return null;
  const other = members.find((m) => m.userId !== myId);
  // 멤버 정보가 나 하나뿐이면(아직 안 불러온 경우 포함) 첫 사람으로 떨어진다.
  return other ?? members[0] ?? null;
}

export function resolveRoomName(input: {
  type: string;
  /** 방에 저장된 공용 이름. */
  name?: string | null;
  members?: readonly NameMember[];
  myId?: string | null;
  /** 내가 부르는 이름(chatDisplayNameStore). */
  myName?: string | null;
  /** 기본 이름조차 못 만들 때 쓸 문구 — 호출부의 i18n 문구를 받는다. */
  fallback?: string;
}): string {
  const mine = input.myName?.trim();
  if (mine) return mine;

  const shared = input.name?.trim();
  if (shared) return shared;

  if (input.type === 'direct') {
    const other = otherMemberOf(input.members, input.myId);
    const n = other?.name?.trim();
    if (n) return n;
  } else {
    return GROUP_FALLBACK_NAME;
  }

  return input.fallback ?? GROUP_FALLBACK_NAME;
}

/**
 * 이름만으로 방을 구분하기 어려울 때 곁들이는 참여자 줄 — "김서연, 박준호 외 3".
 * 기본 이름을 쓰는 방(단체대화방)이 여럿이면 목록이 전부 같은 이름이 되기 때문이다.
 * 보탤 것이 없으면 빈 문자열.
 */
export function memberSummary(
  members: readonly NameMember[] | undefined,
  myId: string | null | undefined,
  shown = 2,
): string {
  if (!members || members.length === 0) return '';
  const others = members.filter((m) => m.userId !== myId);
  if (others.length === 0) return '';
  const names = others.map((m) => m.name?.trim()).filter((n): n is string => !!n);
  if (names.length === 0) return '';
  const head = names.slice(0, shown).join(', ');
  const rest = names.length - shown;
  return rest > 0 ? `${head} 외 ${rest}` : head;
}
